import { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { BoardFiltersButton } from '~/components/Filters/FiltersButton/BoardFiltersButton';
import {
  FiltersDropdownTrigger,
  type FiltersDropdownTriggerProps,
} from '~/components/Filters/FiltersButton/FiltersDropdownTrigger';
import {
  PrivateFiltersDropdown,
  type PrivateFiltersDropdownProps,
} from '~/components/Filters/FiltersButton/PrivateFiltersDropdown';
import { type FilterType, IPTC_FIELDS, sortFiltersByOrder } from '~/components/FiltersMenu/types';
import { useFetchAnyWorkspacePermissions } from '~/hooks/useFetchAnyWorkspacePermissions';
import { useGetAvailableCustomFields } from '~/hooks/useGetAvailableCustomFields';
import { useURLLibraryIdSelector } from '~/hooks/useURLLibraryIdSelector';
import { usePrivateCustomFieldsContext } from '~/providers/PrivateCustomFieldsProvider';
import { centralizedBoardLibraryIdSelector } from '~/store/centralizedBoard/selectors';
import { isKanbanViewSelector } from '~/store/configViews/selectors';
import { type OtherFiltersType } from '~/store/filters/types';
import { getSelectCustomFields } from '~/swr-hooks/customFields/utils';
import { useCanViewPrivateAddOn } from '~/swr-hooks/workspaces/addOns/useCanViewPrivateAddOn';
import { canSeeWorkspaceSmartTags } from '~/utils/permissions/workspacePermissions';
import { useAirSelector } from '~/utils/ReduxUtils';
import { type ItemTypeFilter } from '../types';

export type PrivateFiltersButtonProps = {
  title: string;
  onApplyClick?: () => void;
  availableTypes?: ItemTypeFilter[];
  basicFilters?: FilterType[];
} & Pick<PrivateFiltersDropdownProps, 'secondaryCTA'> &
  Partial<Pick<FiltersDropdownTriggerProps, 'renderTrigger'>>;

const availableOtherFilters: OtherFiltersType[] = ['has-open-comments', 'has-versions', 'is-untagged'];

export const defaultBasicBoardFilters: FilterType[] = [
  'separator1',
  'separator2',
  'assetFileKind',
  'type',
  'uploader',
  'color',
  'customField',
  'tags',
  'orientation',
  'dateCreated',
  'dateModified',
  'dateUploaded',
  'extension',
  'source',
  'other',
];

export const PrivateBoardFiltersButton = memo(
  ({
    title,
    availableTypes,
    secondaryCTA,
    onApplyClick: onApplyFilters,
    basicFilters = defaultBasicBoardFilters,
    renderTrigger = BoardFiltersButton,
  }: PrivateFiltersButtonProps) => {
    const isKanbanView = useAirSelector(isKanbanViewSelector);
    const { customFields } = usePrivateCustomFieldsContext();

    const urlLibraryId = useURLLibraryIdSelector();
    const libraryId = useSelector(centralizedBoardLibraryIdSelector);
    const { getAvailableCustomFields } = useGetAvailableCustomFields({
      libraryId: libraryId || urlLibraryId,
    });

    const selectCustomFields = useMemo(() => {
      const availableCustomFields = getAvailableCustomFields(customFields);

      return availableCustomFields ? getSelectCustomFields(availableCustomFields) : [];
    }, [customFields, getAvailableCustomFields]);

    const { canViewAddOn: canSeePrivateIPTCFields } = useCanViewPrivateAddOn({ key: 'metadata' });
    const { canViewAddOn: canSeePrivatePeople } = useCanViewPrivateAddOn({ key: 'facialRecognition' });
    const { data: permissions } = useFetchAnyWorkspacePermissions();

    const canSeeSmartTags = canSeeWorkspaceSmartTags(permissions);

    const availableFilters = useMemo<FilterType[]>(() => {
      const filters: FilterType[] = [...basicFilters];

      if (canSeePrivatePeople) {
        filters.push('people');
      }

      filters.push(...IPTC_FIELDS);

      if (canSeeSmartTags) {
        filters.push('smartTags');
      }

      filters.push('locations');
      filters.push('assetFileKind');

      return filters.sort(sortFiltersByOrder);
    }, [basicFilters, canSeePrivatePeople, canSeeSmartTags]);

    const disabledFilters = useMemo(() => {
      const disabledFilters: FilterType[] = [];

      if (!canSeePrivateIPTCFields) {
        disabledFilters.push(...IPTC_FIELDS);
      }

      return disabledFilters;
    }, [canSeePrivateIPTCFields]);

    const renderDropdown: FiltersDropdownTriggerProps['renderFiltersDropdown'] = useCallback(
      ({ closePopover }) => (
        <PrivateFiltersDropdown
          availableFilters={availableFilters}
          disabledFilters={disabledFilters}
          otherFilters={availableOtherFilters}
          customFields={selectCustomFields}
          availableTypes={availableTypes}
          secondaryCTA={secondaryCTA}
          onApplyClick={() => {
            closePopover();
            onApplyFilters?.();
          }}
          title={title}
          trackLocation="board"
        />
      ),
      [availableFilters, availableTypes, disabledFilters, onApplyFilters, secondaryCTA, selectCustomFields, title],
    );

    if (isKanbanView) return null;

    return <FiltersDropdownTrigger renderFiltersDropdown={renderDropdown} renderTrigger={renderTrigger} />;
  },
);

PrivateBoardFiltersButton.displayName = 'PrivateBoardFiltersButton';
